import { useT } from "../../../shared/i18n/i18n";
import { CalendarRange, CheckCircle2, Inbox, MoreHorizontal, Pencil, Play, Plus, Repeat2, Search, Trash2, Undo2, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import type { BoardView } from "../../../entities/board/model/use-boards";
import { applySprintMove, sprintStateFor, todayIso, useSprints } from "../../../entities/sprint-planning/model/use-sprints";
import type { Sprint, SprintItem } from "../../../entities/sprint-planning/model/use-sprints";
import { cn } from "../../../shared/lib/cn";
import { useTRPC } from "../../../shared/api/trpc";
import {
  DndSortableList,
  MultiContainerDnd,
  useDndContainer,
  useDndSortableItem,
  type DndContainerState,
} from "../../../shared/ui/multi-container-dnd";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { sprintStateMeta } from "./sprint-state";

const backlogContainerKey = "__backlog__";

type AddCandidate = {
  title: string;
  typeName: string | null;
  workObjectId: string;
};

export function SprintPlanningSurface({
  board,
  onOpenItem,
  spaceBoards,
}: {
  board: BoardView;
  onOpenItem?: (workObjectId: string) => void;
  spaceBoards: BoardView[];
}) {
  const t = useT();
  const sprints = useSprints(board.id);
  const track = sprints.data;
  const orderedSprints = track?.sprints ?? [];
  const serverItems = track?.items;
  // The react-query cache patch lands a microtask after drag end, but dnd-kit
  // measures the drop-animation target synchronously — without this override
  // the released card animates back to its old slot for a frame.
  const [pendingItems, setPendingItems] = useState<SprintItem[] | null>(null);
  useEffect(() => {
    setPendingItems(null);
  }, [serverItems]);
  const items = useMemo(() => pendingItems ?? serverItems ?? [], [pendingItems, serverItems]);

  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingSprint, setEditingSprint] = useState<Sprint | null>(null);
  const [deletingSprint, setDeletingSprint] = useState<Sprint | null>(null);
  const [closingSprint, setClosingSprint] = useState<Sprint | null>(null);
  const [startingSprint, setStartingSprint] = useState<Sprint | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function startSprint(sprint: Sprint, adjustDates: boolean) {
    setActionError(null);
    try {
      await sprints.startSprint({ adjustDates, sprintId: sprint.id });
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Could not start the sprint.");
    }
  }

  async function revertSprintStart(sprint: Sprint) {
    setActionError(null);
    try {
      await sprints.revertSprintStart({ sprintId: sprint.id });
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Could not return the sprint to planned.");
    }
  }

  const resolvedSprintId = useMemo(() => {
    if (selectedSprintId && orderedSprints.some((sprint) => sprint.id === selectedSprintId)) {
      return selectedSprintId;
    }
    const today = todayIso();
    const running = orderedSprints.find((sprint) => {
      const state = sprintStateFor(sprint, today);
      return state === "active" || state === "overdue";
    });
    const planned = orderedSprints.find((sprint) => sprintStateFor(sprint, today) === "planned");
    return (running ?? planned ?? orderedSprints[orderedSprints.length - 1])?.id ?? null;
  }, [orderedSprints, selectedSprintId]);
  const selectedSprint = orderedSprints.find((sprint) => sprint.id === resolvedSprintId) ?? null;

  const itemByWorkObjectId = useMemo(() => new Map(items.map((item) => [item.workObject.id, item])), [items]);
  const backlogItems = items.filter((item) => item.sprintId === null);
  const sprintItems = items.filter((item) => item.sprintId === resolvedSprintId);
  const isSelectedSprintClosed = Boolean(selectedSprint?.closedAt);
  const countBySprintId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      if (item.sprintId) counts.set(item.sprintId, (counts.get(item.sprintId) ?? 0) + 1);
    }
    return counts;
  }, [items]);

  const candidates = useMemo<AddCandidate[]>(() => {
    const inTrack = new Set(items.map((item) => item.workObject.id));
    const seen = new Set<string>();
    const result: AddCandidate[] = [];
    for (const spaceBoard of spaceBoards) {
      if (spaceBoard.kind === "sprint") continue;
      for (const boardItem of spaceBoard.items) {
        const workObject = boardItem.workObject;
        if (seen.has(workObject.id) || inTrack.has(workObject.id)) continue;
        seen.add(workObject.id);
        result.push({ title: workObject.title, typeName: workObject.type?.name ?? null, workObjectId: workObject.id });
      }
    }
    return result.sort((a, b) => a.title.localeCompare(b.title));
  }, [items, spaceBoards]);

  // A closed sprint's column is not a drop target and its cards do not drag.
  const containers: DndContainerState[] = [
    { itemIds: backlogItems.map((item) => item.workObject.id), key: backlogContainerKey },
    ...(selectedSprint && !isSelectedSprintClosed
      ? [{ itemIds: sprintItems.map((item) => item.workObject.id), key: selectedSprint.id }]
      : []),
  ];

  const defaultCreateRange = useMemo(() => {
    const lastEnd = orderedSprints.reduce<string | null>(
      (latest, sprint) => (latest === null || sprint.endsAt > latest ? sprint.endsAt : latest),
      null,
    );
    const start = lastEnd && lastEnd >= todayIso() ? isoAddDays(lastEnd, 1) : todayIso();
    return { from: start, to: isoAddDays(start, 13) };
  }, [orderedSprints]);

  function moveTargets(current: SprintItem) {
    return [
      ...(current.sprintId !== null ? [{ id: null, name: t("sprint.backlog") }] : []),
      ...orderedSprints
        .filter((sprint) => sprint.id !== current.sprintId && !sprint.closedAt)
        .map((sprint) => ({ id: sprint.id, name: sprint.name })),
    ];
  }

  // Sum of the track's primary estimate axis for a column of items.
  const primaryEstimate = track?.primaryEstimate ?? null;
  function estimateSumLabel(workObjectIds: string[]): string | null {
    if (!primaryEstimate) return null;
    const sum = workObjectIds.reduce((total, id) => total + (primaryEstimate.values[id] ?? 0), 0);
    if (sum === 0) return null;
    return `Σ ${Math.round(sum * 10) / 10}${primaryEstimate.unit ?? ""}`;
  }

  const nextOpenSprint = closingSprint
    ? orderedSprints.find((sprint) => !sprint.closedAt && sprint.id !== closingSprint.id && sprint.startsAt >= closingSprint.startsAt) ??
      null
    : null;

  async function completeSprint(
    sprint: Sprint,
    carry: "backlog" | "new" | "next",
    newRange: { from: string; to: string },
    reason: string,
  ) {
    let carryTarget = "backlog";
    if (carry === "next" && nextOpenSprint) {
      carryTarget = nextOpenSprint.id;
    } else if (carry === "new") {
      const created = await sprints.createSprint({ endsAt: newRange.to, startsAt: newRange.from });
      carryTarget = created.id;
    }
    await sprints.closeSprint({ carryTarget, reason: reason.trim() || undefined, sprintId: sprint.id });
    if (carryTarget !== "backlog") {
      setSelectedSprintId(carryTarget);
    }
  }

  return (
    <section className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col">
      <MultiContainerDnd
        containers={containers}
        onDragEnd={(result) => {
          if (!result) return;
          const targetIds = result.containers.find((container) => container.key === result.targetContainerKey)?.itemIds ?? [];
          const droppedAt = targetIds.indexOf(result.itemId);
          if (result.sourceContainerKey === result.targetContainerKey && droppedAt === -1) return;
          const move = {
            position:
              droppedAt === -1
                ? null
                : { nextWorkObjectId: targetIds[droppedAt + 1] ?? null, previousWorkObjectId: targetIds[droppedAt - 1] ?? null },
            sprintId: result.targetContainerKey === backlogContainerKey ? null : result.targetContainerKey,
            workObjectId: result.itemId,
          };
          setPendingItems(applySprintMove(items, move));
          void sprints.moveItem(move);
        }}
        renderOverlay={(itemId) => {
          const item = itemByWorkObjectId.get(itemId);
          // No fixed width: the DragOverlay wrapper takes the source card's
          // measured size, so the clone neither shrinks on lift nor snaps on drop.
          return item ? <SprintCardContent item={item} /> : null;
        }}
      >
        {(liveContainers) => {
          const liveBacklogIds = liveContainers.find((container) => container.key === backlogContainerKey)?.itemIds ?? [];
          const liveSprintIds = selectedSprint
            ? liveContainers.find((container) => container.key === selectedSprint.id)?.itemIds ?? []
            : [];
          return (
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              {actionError ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
                  <span className="min-w-0 truncate">{actionError}</span>
                  <button aria-label={t("sprint.dismiss")} className="shrink-0 text-destructive/70 hover:text-destructive" onClick={() => setActionError(null)} type="button">
                    <X className="size-3.5" />
                  </button>
                </div>
              ) : null}
              <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-2">
              <PlanningPane
                count={liveBacklogIds.length}
                estimate={estimateSumLabel(liveBacklogIds)}
                headerActions={
                  <AddItemsPicker
                    candidates={candidates}
                    onAdd={(candidate) => sprints.addItems({ sprintId: null, workObjectIds: [candidate.workObjectId] })}
                  />
                }
                icon={<Inbox className="size-4 text-muted-foreground" />}
                title={t("sprint.backlog")}
              >
                <ItemsColumn
                  containerKey={backlogContainerKey}
                  emptyHint={
                    candidates.length > 0
                      ? t("sprint.addItemsHint")
                      : t("sprint.newItemsLandHere")
                  }
                  itemByWorkObjectId={itemByWorkObjectId}
                  itemIds={liveBacklogIds}
                  moveTargets={moveTargets}
                  onMove={(workObjectId, sprintId) => void sprints.moveItem({ sprintId, workObjectId })}
                  onOpenItem={onOpenItem}
                  onRemove={(workObjectId) => void sprints.removeItem(workObjectId)}
                />
              </PlanningPane>

              <PlanningPane
                count={selectedSprint ? (isSelectedSprintClosed ? sprintItems.length : liveSprintIds.length) : null}
                estimate={
                  selectedSprint
                    ? estimateSumLabel(isSelectedSprintClosed ? sprintItems.map((item) => item.workObject.id) : liveSprintIds)
                    : null
                }
                headerActions={
                  selectedSprint && !isSelectedSprintClosed ? (
                    <>
                      {/* Starting is an explicit operation: it freezes what the
                          sprint committed to, so it always asks first. */}
                      {sprintStateFor(selectedSprint) === "planned" ? (
                        <Button className="h-6 gap-1 px-2 text-xs" onClick={() => setStartingSprint(selectedSprint)} size="sm">
                          <Play className="size-3.5" />
                          {t("sprint.start")}
                        </Button>
                      ) : (
                        <Button className="h-6 gap-1 px-2 text-xs" onClick={() => setClosingSprint(selectedSprint)} size="sm" variant="outline">
                          <CheckCircle2 className="size-3.5" />
                          {t("sprint.complete")}
                        </Button>
                      )}
                      <AddItemsPicker
                        candidates={candidates}
                        onAdd={(candidate) => sprints.addItems({ sprintId: selectedSprint.id, workObjectIds: [candidate.workObjectId] })}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          render={
                            <Button aria-label={t("sprint.options")} className="size-6 text-muted-foreground" size="icon-sm" variant="ghost">
                              <MoreHorizontal />
                            </Button>
                          }
                        />
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingSprint(selectedSprint)}>
                            <Pencil data-icon="inline-start" />
                            {t("sprint.edit")}
                          </DropdownMenuItem>
                          {/* Undo for a start that should not have happened: the
                              sprint goes back to planned and its commitment
                              baseline is dropped. */}
                          {selectedSprint.startedAt ? (
                            <DropdownMenuItem onClick={() => void revertSprintStart(selectedSprint)}>
                              <Undo2 data-icon="inline-start" />
                              {t("sprint.returnToPlanned")}
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <Repeat2 data-icon="inline-start" />
                              {t("sprint.autoPlan")}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="w-40">
                              <DropdownMenuRadioGroup
                                onValueChange={(value) => void sprints.updateCadence(value === "off" || value == null ? null : Number(value))}
                                value={String(track?.board.sprintCadenceWeeks ?? "off")}
                              >
                                <DropdownMenuRadioItem value="off">{t("sprint.off")}</DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="1">{t("sprint.everyWeek")}</DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="2">{t("sprint.every2")}</DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="3">{t("sprint.every3")}</DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="4">{t("sprint.every4")}</DropdownMenuRadioItem>
                              </DropdownMenuRadioGroup>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setDeletingSprint(selectedSprint)} variant="destructive">
                            <Trash2 data-icon="inline-start" />
                            {t("sprint.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  ) : null
                }
                switcher={
                  <SprintChipRow
                    countBySprintId={countBySprintId}
                    onCreate={() => setIsCreateOpen(true)}
                    onSelect={setSelectedSprintId}
                    selectedSprintId={resolvedSprintId}
                    sprints={orderedSprints}
                  />
                }
                subtitle={selectedSprint ? <SprintMeta sprint={selectedSprint} /> : null}
                title={selectedSprint?.name ?? "Sprints"}
              >
                {selectedSprint && isSelectedSprintClosed ? (
                  <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-2">
                    {sprintItems.map((item) => (
                      <button
                        className="text-left"
                        key={item.workObject.id}
                        onClick={() => onOpenItem?.(item.workObject.id)}
                        type="button"
                      >
                        <SprintCardContent item={item} />
                      </button>
                    ))}
                    {sprintItems.length === 0 ? (
                      <div className="flex flex-1 items-center justify-center p-4">
                        <p className="max-w-56 text-center text-xs text-muted-foreground/80">
                          {t("sprint.nothingCompleted")}
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : selectedSprint ? (
                  <ItemsColumn
                    containerKey={selectedSprint.id}
                    emptyHint="Drag items from the backlog to plan this sprint."
                    itemByWorkObjectId={itemByWorkObjectId}
                    itemIds={liveSprintIds}
                    moveTargets={moveTargets}
                    onMove={(workObjectId, sprintId) => void sprints.moveItem({ sprintId, workObjectId })}
                    onOpenItem={onOpenItem}
                    onRemove={(workObjectId) => void sprints.removeItem(workObjectId)}
                  />
                ) : (
                  <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
                    <CalendarRange className="size-8 text-muted-foreground/50" />
                    <div>
                      <p className="text-sm font-medium">{t("sprint.noSprints")}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{t("sprint.createFirst")}</p>
                    </div>
                    <Button onClick={() => setIsCreateOpen(true)} size="sm">
                      <Plus data-icon="inline-start" />
                      {t("sprint.newSprint")}
                    </Button>
                  </div>
                )}
              </PlanningPane>
              </div>
            </div>
          );
        }}
      </MultiContainerDnd>

      <SprintDialog
        initialRange={defaultCreateRange}
        key={`create:${isCreateOpen}`}
        onOpenChange={setIsCreateOpen}
        onSubmit={async (values) => {
          const sprint = await sprints.createSprint(values);
          setSelectedSprintId(sprint.id);
        }}
        open={isCreateOpen}
        placeholderName={`Sprint ${orderedSprints.length + 1}`}
        title={t("sprint.newSprint")}
      />
      <SprintDialog
        initialRange={editingSprint ? { from: editingSprint.startsAt, to: editingSprint.endsAt } : defaultCreateRange}
        initialValues={editingSprint}
        key={`edit:${editingSprint?.id ?? "none"}`}
        onOpenChange={(open) => {
          if (!open) setEditingSprint(null);
        }}
        onSubmit={async (values) => {
          if (!editingSprint) return;
          await sprints.updateSprint({ ...values, sprintId: editingSprint.id });
        }}
        open={editingSprint !== null}
        placeholderName={editingSprint?.name ?? "Sprint"}
        title={t("sprint.edit")}
      />

      <AlertDialog onOpenChange={(open) => (!open ? setDeletingSprint(null) : null)} open={deletingSprint !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deletingSprint?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>{t("sprint.deleteHint")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingSprint) void sprints.archiveSprint(deletingSprint.id);
                setDeletingSprint(null);
              }}
              variant="destructive"
            >
              {t("sprint.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {closingSprint ? (
        <CompleteSprintDialog
          items={items.filter((item) => item.sprintId === closingSprint.id)}
          key={closingSprint.id}
          nextOpenSprint={nextOpenSprint}
          onComplete={(carry, newRange, reason) => completeSprint(closingSprint, carry, newRange, reason)}
          onOpenChange={(open) => (!open ? setClosingSprint(null) : null)}
          sprint={closingSprint}
        />
      ) : null}

      {startingSprint ? (
        <StartSprintDialog
          boardId={board.id}
          key={startingSprint.id}
          onOpenChange={(open) => (!open ? setStartingSprint(null) : null)}
          onStart={(adjustDates) => startSprint(startingSprint, adjustDates)}
          sprint={startingSprint}
        />
      ) : null}
    </section>
  );
}

// Starting a sprint freezes its commitment baseline and can push later
// sprints, so it says exactly what will happen before it happens.
function StartSprintDialog({
  boardId,
  onOpenChange,
  onStart,
  sprint,
}: {
  boardId: string;
  onOpenChange: (open: boolean) => void;
  onStart: (adjustDates: boolean) => Promise<void>;
  sprint: Sprint;
}) {
  const t = useT();
  const trpc = useTRPC();
  const [adjustDates, setAdjustDates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const preview = useQuery(trpc.sprints.startSprintPreview.queryOptions({ adjustDates, sprintId: sprint.id }));
  void boardId;

  return (
    <Dialog onOpenChange={onOpenChange} open>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Start “{sprint.name}”?</DialogTitle>
          <DialogDescription>
            {preview.data
              ? `${preview.data.committedCount} ${preview.data.committedCount === 1 ? "item" : "items"} become this sprint's commitment. Anything added later counts as added scope.`
              : "Loading what starting will change…"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{t("sprint.window")}</span>
            <span className="font-medium">
              {preview.data ? formatSprintRange(preview.data.nextStartsAt, preview.data.nextEndsAt) : formatSprintRange(sprint.startsAt, sprint.endsAt)}
            </span>
          </div>
          <label className="flex items-start gap-2 rounded-md border border-border/60 px-3 py-2 text-left">
            <Checkbox checked={adjustDates} onCheckedChange={(checked) => setAdjustDates(checked === true)} />
            <span className="min-w-0">
              <span className="block text-sm font-medium">{t("sprint.beginToday")}</span>
              <span className="block text-xs text-muted-foreground">
                {t("sprint.beginTodayHint")}
              </span>
            </span>
          </label>
          {preview.data && preview.data.shiftedSprints.length > 0 ? (
            <div className="flex flex-col gap-1 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              <span className="font-medium">{t("sprint.theseMoveToo")}</span>
              {preview.data.shiftedSprints.map((shifted) => (
                <span key={shifted.id}>
                  {shifted.name || "Sprint"} → {formatSprintRange(shifted.startsAt, shifted.endsAt)}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            {t("common.cancel")}
          </Button>
          <Button
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                await onStart(adjustDates);
                onOpenChange(false);
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            <Play data-icon="inline-start" />
            {t("sprint.start")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteSprintDialog({
  items,
  nextOpenSprint,
  onComplete,
  onOpenChange,
  sprint,
}: {
  items: SprintItem[];
  nextOpenSprint: Sprint | null;
  onComplete: (carry: "backlog" | "new" | "next", newRange: { from: string; to: string }, reason: string) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  sprint: Sprint;
}) {
  const t = useT();
  const doneCount = items.filter((item) => item.status.category === "done" || item.status.category === "closed").length;
  const openCount = items.length - doneCount;
  const newRange = useMemo(() => {
    const start = isoAddDays(sprint.endsAt, 1);
    return { from: start, to: isoAddDays(start, isoDaysBetween(sprint.startsAt, sprint.endsAt)) };
  }, [sprint]);
  const carryOptions: Array<{ description: string; label: string; value: "backlog" | "new" | "next" }> = [
    ...(nextOpenSprint
      ? [{ description: formatSprintRange(nextOpenSprint.startsAt, nextOpenSprint.endsAt), label: nextOpenSprint.name, value: "next" as const }]
      : [{ description: formatSprintRange(newRange.from, newRange.to), label: t("sprint.newSprint"), value: "new" as const }]),
    { description: t("sprint.planItLater"), label: t("sprint.backlog"), value: "backlog" as const },
  ];
  const [carry, setCarry] = useState<"backlog" | "new" | "next">(carryOptions[0]?.value ?? "backlog");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <Dialog onOpenChange={onOpenChange} open>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Complete “{sprint.name}”?</DialogTitle>
          <DialogDescription>
            {items.length === 0
              ? "The sprint is empty — it will simply be closed."
              : `${items.length} ${items.length === 1 ? "item" : "items"} · ${doneCount} done · ${openCount} open. The result is snapshotted for reports and cannot be changed afterwards.`}
          </DialogDescription>
        </DialogHeader>
        {openCount > 0 ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              Move {openCount} open {openCount === 1 ? "item" : "items"} to
            </p>
            {carryOptions.map((option) => (
              <button
                className={cn(
                  "flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                  carry === option.value ? "border-primary/50 bg-primary/5" : "border-border/60 hover:bg-accent/40",
                )}
                key={option.value}
                onClick={() => setCarry(option.value)}
                type="button"
              >
                <span
                  className={cn(
                    "size-3.5 shrink-0 rounded-full border",
                    carry === option.value ? "border-4 border-primary" : "border-muted-foreground/40",
                  )}
                />
                <span className="min-w-0 flex-1 truncate font-medium">{option.label}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{option.description}</span>
              </button>
            ))}
          </div>
        ) : null}
        {openCount > 0 ? (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="sprint-close-reason">
              {t("sprint.whyClosingOpen")}
            </label>
            <Input
              autoComplete="off"
              id="sprint-close-reason"
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("sprint.releaseCutMoved")}
              value={reason}
            />
          </div>
        ) : null}
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            {t("common.cancel")}
          </Button>
          <Button
            disabled={isSubmitting || (openCount > 0 && reason.trim() === "")}
            onClick={async () => {
              setIsSubmitting(true);
              setError(null);
              try {
                await onComplete(carry, newRange, reason);
                onOpenChange(false);
              } catch (submitError) {
                setError(submitError instanceof Error ? submitError.message : "Could not complete the sprint.");
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            <CheckCircle2 data-icon="inline-start" />
            {t("sprint.completeSprint")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanningPane({
  children,
  count,
  estimate,
  headerActions,
  icon,
  subtitle,
  switcher,
  title,
}: {
  children: ReactNode;
  count: number | null;
  estimate?: string | null;
  headerActions?: ReactNode;
  icon?: ReactNode;
  subtitle?: ReactNode;
  switcher?: ReactNode;
  title: string;
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-border/70 bg-card">
      {switcher}
      <div className="flex items-center gap-2 px-3 pb-1 pt-2.5">
        {icon}
        <span className="min-w-0 truncate text-sm font-medium">{title}</span>
        {count !== null ? <span className="text-xs tabular-nums text-muted-foreground">{count}</span> : null}
        {estimate ? <span className="text-xs tabular-nums text-muted-foreground/80">{estimate}</span> : null}
        <div className="ml-auto flex items-center gap-1">{headerActions}</div>
      </div>
      {subtitle ? <div className="px-3 pb-1">{subtitle}</div> : null}
      {children}
    </div>
  );
}

function SprintChipRow({
  countBySprintId,
  onCreate,
  onSelect,
  selectedSprintId,
  sprints,
}: {
  countBySprintId: Map<string, number>;
  onCreate: () => void;
  onSelect: (sprintId: string) => void;
  selectedSprintId: string | null;
  sprints: Sprint[];
}) {
  const t = useT();
  const today = todayIso();
  return (
    <div className="flex items-center gap-1 overflow-x-auto bg-muted/40 px-2 py-1.5">
      {sprints.map((sprint) => {
        const state = sprintStateFor(sprint, today);
        const count = countBySprintId.get(sprint.id) ?? 0;
        return (
          <button
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors",
              sprint.id === selectedSprintId
                ? "bg-background font-medium text-foreground shadow-xs"
                : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
            )}
            key={sprint.id}
            onClick={() => onSelect(sprint.id)}
            type="button"
          >
            <span className={cn("size-1.5 rounded-full", sprintStateMeta[state].dotClassName)} />
            {sprint.name}
            {count > 0 ? <span className="text-[0.65rem] tabular-nums text-muted-foreground/80">{count}</span> : null}
          </button>
        );
      })}
      <Button aria-label={t("sprint.newSprint")} className="size-6 shrink-0 text-muted-foreground" onClick={onCreate} size="icon-sm" variant="ghost">
        <Plus />
      </Button>
    </div>
  );
}

function SprintMeta({ sprint }: { sprint: Sprint }) {
  const t = useT();
  const state = sprintStateFor(sprint);
  const meta = sprintStateMeta[state];
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className={cn("rounded-full px-1.5 py-0.5 text-[0.65rem] font-medium", meta.badgeClassName)}>{t(meta.labelKey)}</span>
        <span>{formatSprintRange(sprint.startsAt, sprint.endsAt)}</span>
        {state === "active" ? <span>· {daysLeftLabel(sprint.endsAt)}</span> : null}
      </div>
      {sprint.goal ? <p className="min-w-0 truncate text-xs text-muted-foreground/80">{sprint.goal}</p> : null}
    </div>
  );
}

function ItemsColumn({
  containerKey,
  emptyHint,
  itemByWorkObjectId,
  itemIds,
  moveTargets,
  onMove,
  onOpenItem,
  onRemove,
}: {
  containerKey: string;
  emptyHint: string;
  itemByWorkObjectId: Map<string, SprintItem>;
  itemIds: string[];
  moveTargets: (item: SprintItem) => Array<{ id: string | null; name: string }>;
  onMove: (workObjectId: string, sprintId: string | null) => void;
  onOpenItem?: (workObjectId: string) => void;
  onRemove: (workObjectId: string) => void;
}) {
  const { isOver, setNodeRef } = useDndContainer(containerKey, { childCount: itemIds.length });
  return (
    <div
      className={cn("flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-2 transition-colors", isOver && "bg-accent/40")}
      ref={setNodeRef}
    >
      <DndSortableList itemIds={itemIds}>
        {itemIds.map((workObjectId) => {
          const item = itemByWorkObjectId.get(workObjectId);
          if (!item) return null;
          return (
            <SprintItemCard
              item={item}
              key={workObjectId}
              moveTargets={moveTargets(item)}
              onMove={(sprintId) => onMove(workObjectId, sprintId)}
              onOpen={onOpenItem ? () => onOpenItem(workObjectId) : undefined}
              onRemove={() => onRemove(workObjectId)}
            />
          );
        })}
      </DndSortableList>
      {itemIds.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-4">
          <p className="max-w-56 text-center text-xs text-muted-foreground/80">{emptyHint}</p>
        </div>
      ) : null}
    </div>
  );
}

function SprintItemCard({
  item,
  moveTargets,
  onMove,
  onOpen,
  onRemove,
}: {
  item: SprintItem;
  moveTargets: Array<{ id: string | null; name: string }>;
  onMove: (sprintId: string | null) => void;
  onOpen?: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  const sortable = useDndSortableItem(item.workObject.id);
  return (
    <div
      className={cn("touch-none", sortable.isDragSource && "opacity-40", onOpen && "cursor-pointer")}
      onClick={(event) => {
        if (!onOpen || sortable.wasRecentlyDragged()) return;
        if ((event.target as HTMLElement).closest("button")) return;
        onOpen();
      }}
      ref={sortable.setNodeRef}
      style={sortable.style}
      {...sortable.dragProps}
    >
      <SprintCardContent
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  aria-label={t("common.optionsFor", { name: item.workObject.title })}
                  className="size-6 text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover/sprint-card:opacity-100 aria-expanded:opacity-100"
                  size="icon-sm"
                  variant="ghost"
                >
                  <MoreHorizontal />
                </Button>
              }
            />
            <DropdownMenuContent align="end">
              {moveTargets.map((target) => (
                <DropdownMenuItem key={target.id ?? "backlog"} onClick={() => onMove(target.id)}>
                  Move to {target.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onRemove} variant="destructive">
                {t("sprint.removeFromBoard")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
        item={item}
      />
    </div>
  );
}

function SprintCardContent({ actions, item }: { actions?: ReactNode; item: SprintItem }) {
  return (
    <div className="group/sprint-card flex flex-col gap-1.5 rounded-md border border-border/60 bg-background p-2.5 shadow-xs">
      {/* min-h matches the actions button so the drag-overlay clone (which
          renders without actions) keeps exactly the source card's height. */}
      <div className="flex min-h-6 items-start gap-1.5">
        <span className="min-w-0 flex-1 text-sm leading-snug">{item.workObject.title}</span>
        {actions}
      </div>
      <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
        {item.workObject.type ? (
          <Badge className="shrink-0 px-1.5 py-0 text-[0.65rem]" variant="secondary">
            {item.workObject.type.name}
          </Badge>
        ) : null}
        <span className="flex min-w-0 items-center gap-1">
          <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: item.status.color }} />
          <span className="truncate">{item.status.name}</span>
        </span>
      </div>
    </div>
  );
}

function AddItemsPicker({ candidates, onAdd }: { candidates: AddCandidate[]; onAdd: (candidate: AddCandidate) => Promise<void> }) {
  const t = useT();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const normalized = query.trim().toLowerCase();
  const filtered = (normalized ? candidates.filter((candidate) => candidate.title.toLowerCase().includes(normalized)) : candidates).slice(
    0,
    50,
  );

  return (
    <Popover
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setQuery("");
      }}
      open={isOpen}
    >
      <PopoverTrigger
        render={
          <Button className="h-6 gap-1 px-2 text-xs text-muted-foreground" size="sm" variant="ghost">
            <Plus className="size-3.5" />
            {t("sprint.addItems")}
          </Button>
        }
      />
      <PopoverContent align="end" className="w-80 p-0">
        <div className="relative p-2 pb-1.5">
          <Search className="pointer-events-none absolute left-4 top-1/2 size-3.5 -translate-y-[calc(50%+1px)] text-muted-foreground" />
          <input
            autoFocus
            className="h-8 w-full rounded-md bg-muted/50 pl-7 pr-2 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:bg-muted/70"
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("sprint.searchItems")}
            value={query}
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">
              {candidates.length === 0 ? t("sprint.allAlreadyOnBoard") : t("sprint.noItemsMatch")}
            </p>
          ) : (
            filtered.map((candidate) => (
              <button
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent disabled:opacity-50"
                disabled={pendingId === candidate.workObjectId}
                key={candidate.workObjectId}
                onClick={async () => {
                  setPendingId(candidate.workObjectId);
                  try {
                    await onAdd(candidate);
                  } finally {
                    setPendingId(null);
                  }
                }}
                type="button"
              >
                <span className="min-w-0 flex-1 truncate">{candidate.title}</span>
                {candidate.typeName ? (
                  <Badge className="shrink-0 px-1.5 py-0 text-[0.65rem]" variant="secondary">
                    {candidate.typeName}
                  </Badge>
                ) : null}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SprintDialog({
  initialRange,
  initialValues,
  onOpenChange,
  onSubmit,
  open,
  placeholderName,
  title,
}: {
  initialRange: { from: string; to: string };
  initialValues?: Sprint | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: { endsAt: string; goal?: string; name?: string; startsAt: string }) => Promise<void>;
  open: boolean;
  placeholderName: string;
  title: string;
}) {
  const t = useT();
  const [name, setName] = useState(initialValues?.name ?? "");
  const [goal, setGoal] = useState(initialValues?.goal ?? "");
  const [range, setRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: fromIsoDate(initialRange.from),
    to: fromIsoDate(initialRange.to),
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!range.from || !range.to) {
      setError("Pick the sprint start and end dates.");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        endsAt: toIsoDate(range.to),
        goal: goal.trim(),
        name: name.trim() || undefined,
        startsAt: toIsoDate(range.from),
      });
      onOpenChange(false);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not save the sprint.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t("sprint.sprintsBackToBack")}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <Input maxLength={80} onChange={(event) => setName(event.target.value)} placeholder={placeholderName} value={name} />
          <DatePicker
            mode="range"
            onChange={(next) => setRange({ from: next?.from, to: next?.to })}
            value={range.from ? { from: range.from, to: range.to } : undefined}
          />
          <Textarea
            className="min-h-16"
            maxLength={1000}
            onChange={(event) => setGoal(event.target.value)}
            placeholder={t("sprint.goal")}
            value={goal}
          />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
              {t("common.cancel")}
            </Button>
            <Button disabled={isSubmitting} type="submit">
              {initialValues ? t("common.save") : t("sprint.createSprint")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function fromIsoDate(value: string): Date | undefined {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

function toIsoDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function isoAddDays(value: string, days: number) {
  const date = fromIsoDate(value) ?? new Date();
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
}

function isoDaysBetween(from: string, to: string) {
  const start = fromIsoDate(from);
  const end = fromIsoDate(to);
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / 86_400_000);
}

export function formatSprintRange(startsAt: string, endsAt: string) {
  const start = fromIsoDate(startsAt);
  const end = fromIsoDate(endsAt);
  if (!start || !end) return `${startsAt} – ${endsAt}`;
  const sameYear = start.getFullYear() === end.getFullYear();
  const startLabel = start.toLocaleDateString(undefined, { day: "numeric", month: "short", year: sameYear ? undefined : "numeric" });
  const endLabel = end.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return `${startLabel} – ${endLabel}`;
}

function daysLeftLabel(endsAt: string) {
  const end = fromIsoDate(endsAt);
  if (!end) return "";
  const today = fromIsoDate(todayIso());
  if (!today) return "";
  const days = Math.round((end.getTime() - today.getTime()) / 86_400_000);
  if (days <= 0) return "ends today";
  return days === 1 ? "1 day left" : `${days} days left`;
}
